/** One stage panel of the booking spine (plan §1.1) — the records core's `RecordPanel`
 *  (Editable fields, the muted "From <restech>" vendor block, the System block) over the
 *  booking's FieldSlots: its displays, value actions and record-changing action controls.
 *  The panel is the stage's anchor — the spine nodes and `1`–`5` scroll to it and focus
 *  it. */
import type { ReactNode } from 'react';

import type { BookingRecordStage } from '@/api/bookingRecord';
import { ActionControl } from '@/features/records/actions/ActionControl';
import { actionOfField } from '@/features/records/actions/model';
import { useRecordActions } from '@/features/records/actions/RecordActionsProvider';
import { FieldCellGrid } from '@/features/records/fields/FieldCellGrid';
import { FieldSlot } from '@/features/records/fields/FieldSlot';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { FIELD_GRID_SX } from '@/features/records/panels/fieldGrid';
import { RecordPanel, type RecordFieldGroup } from '@/features/records/panels/RecordPanel';
import type { ViewFieldRef, ViewSection } from '@/features/records/views/types';

import { stageAnchorId } from '../views/spineLayout';

import { editableDisplay, fieldActions, fieldDisplay, omittedDisplay, type BookingDisplayContext } from './displays';

/** Widgets whose input wants the whole row (the guest's contacts list too, in both
 *  modes). */
const WIDE_WIDGETS = new Set(['tags', 'textarea', 'tour', 'contacts']);
/** Read-only values that read as lists. */
const WIDE_KEYS = new Set(['waivers', 'extras', 'attachments', 'pricing', 'order', 'reviews', 'checkin_window']);

export interface StagePanelProps {
  section: ViewSection<BookingRecordStage>;
  display: BookingDisplayContext;
  /** Right side of the panel header ("times in Europe/London"). */
  meta?: ReactNode;
  /** Renderings that replace the generic one for a key (the order row, waivers). */
  displays?: Partial<Record<string, ReactNode>>;
  /** Sub-groups of the editable block (the guest vs this booking). Default: one group. */
  groupEditable?: (fields: ViewFieldRef[]) => RecordFieldGroup[];
  /** Sub-groups of the vendor block (the booking's identity vs its money). */
  groupVendor?: (fields: ViewFieldRef[]) => RecordFieldGroup[];
  /** After the editable fields (party rows). */
  afterEditable?: ReactNode;
  /** After the vendor fields (the tickets grid). */
  afterVendor?: ReactNode;
  /** After the system fields (related bookings, links). */
  afterSystem?: ReactNode;
}

export function StagePanel({ section, display, displays, ...rest }: StagePanelProps) {
  return (
    <RecordPanel
      section={section}
      anchorId={stageAnchorId(section.id)}
      renderFields={(g) => <FieldGrid fields={g.fields} display={display} displays={displays} />}
      {...rest}
    />
  );
}

export function FieldGrid({
  fields,
  display,
  displays,
}: {
  fields: ViewFieldRef[];
  display: BookingDisplayContext;
  displays?: Partial<Record<string, ReactNode>> | undefined;
}) {
  const { fieldByKey, config } = useRecordEditor();
  const { isAvailable } = useRecordActions();
  return (
    <FieldCellGrid
      sx={FIELD_GRID_SX}
      cells={fields.map((f) => {
        const field = fieldByKey.get(f.key);
        const rec = `${f.entity}:${f.id}`;
        const found = field ? actionOfField(field, config.actions) : undefined;
        // Availability does not depend on the mode: a control is there in both or neither.
        const action = found && isAvailable(found, rec) ? found : undefined;
        const readOnly = field?.kind === 'vendor' || field?.kind === 'derived';
        const override = displays?.[f.key];
        const own = f.entity === 'booking' && f.id === display.editContext.booking_id;
        const hidden = omittedDisplay(f, display);
        const shown =
          hidden !== undefined
            ? hidden
            : override !== undefined
              ? override
              : readOnly
                ? fieldDisplay(f.key, display)
                : own
                  ? editableDisplay(f.key, display)
                  : undefined;
        const actions = readOnly ? undefined : fieldActions(f.key, display);
        // An input keeps its width in both modes; a read-only list takes the row only
        // when it has something to list (an empty one is a plain "—"). An action of the
        // opened booking (on-site status, owner) takes the row for its buttons — in both
        // modes, decided by the catalog, so nothing moves on Edit.
        const wide =
          Boolean(field?.widget && WIDE_WIDGETS.has(field.widget)) ||
          (WIDE_KEYS.has(f.key) && shown !== null && shown !== undefined && shown !== '') ||
          (action !== undefined && action.scope !== 'participant');
        return {
          key: `${f.entity}:${f.id}:${f.key}`,
          rec,
          path: f.path,
          wide,
          node: (
            <FieldSlot
              fieldKey={f.key}
              entity={f.entity}
              id={f.id}
              path={f.path}
              inlineChoices={false}
              {...(shown !== undefined ? { display: shown } : {})}
              {...(actions ? { actions } : {})}
              {...(field && action ? { trailing: <ActionControl field={field} rec={rec} /> } : {})}
            />
          ),
        };
      })}
    />
  );
}
